import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TableOfContentsComponent, TocItem } from '@examdojo/core/ui/table-of-contents';
import { assertNonNullable } from '@examdojo/util/assert';
import { TableOfContentData } from '@tiptap-pro/extension-table-of-contents';
import { Editor } from '@tiptap/core';
import { fromEvent, merge } from 'rxjs';
import { debounceTime, filter, switchMap, tap, throttleTime } from 'rxjs/operators';
import { TextSelection } from 'prosemirror-state';

@Component({
  selector: 'dojo-tiptap-table-of-contents',
  templateUrl: './tiptap-table-of-contents.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableOfContentsComponent],
})
export class TiptapTableOfContentsComponent {
  constructor() {
    merge(this.setInitialActiveHeadings(), this.updateActiveHeadingsOnScroll()).pipe(takeUntilDestroyed()).subscribe();
  }

  readonly tableOfContentData = input.required<TableOfContentData>();
  readonly editor = input.required<Editor>();
  readonly scrollableParent = input<HTMLElement>();

  /** Set of all currently active (visible) heading IDs. */
  private readonly activeAnchorIds = signal<ReadonlySet<string>>(new Set());

  /** Items mapped to generic TocItem[] with scroll-based multi-active state. */
  readonly tocItems = computed<TocItem[]>(() => {
    const data = this.tableOfContentData();
    const activeIds = this.activeAnchorIds();
    return data.map((item) => ({
      id: item.id,
      level: item.level,
      textContent: item.textContent,
      isActive: activeIds.has(item.id),
    }));
  });

  onItemClick(id: string): void {
    const editor = this.editor();
    const scrollableParent = this.scrollableParent();

    if (!scrollableParent) {
      return;
    }

    const element = editor.view.dom.querySelector(`[data-toc-id="${id}"`);
    assertNonNullable(element, 'element');

    const pos = editor.view.posAtDOM(element, 0);

    // set focus
    const tr = editor.view.state.tr;
    tr.setSelection(new TextSelection(tr.doc.resolve(pos)));
    editor.view.dispatch(tr);
    editor.view.focus();

    const editorElement = editor.view.dom;

    const targetRect = element.getBoundingClientRect();
    const scrollableParentRect = scrollableParent.getBoundingClientRect();
    const targetPositionRelativeToParent = targetRect.top - scrollableParentRect.top + scrollableParent.scrollTop;
    const editorPositionRelativeToParent =
      editorElement.getBoundingClientRect().top - scrollableParentRect.top + scrollableParent.scrollTop;

    const targetPosition = targetPositionRelativeToParent - editorPositionRelativeToParent;

    scrollableParent.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  }

  // ---------------------------------------------------------------------------
  // Scroll-based active heading detection (multi-active)
  // ---------------------------------------------------------------------------

  private setInitialActiveHeadings() {
    return toObservable(this.tableOfContentData).pipe(
      filter((data) => data.length > 0),
      debounceTime(0),
      tap(() => this.updateActiveHeadings()),
    );
  }

  private updateActiveHeadingsOnScroll() {
    return toObservable(this.scrollableParent).pipe(
      filter(Boolean),
      switchMap((parent) =>
        fromEvent(parent, 'scroll').pipe(
          throttleTime(80, undefined, { leading: true, trailing: true }),
          tap(() => this.updateActiveHeadings()),
        ),
      ),
    );
  }

  private updateActiveHeadings(): void {
    const data = this.tableOfContentData();
    const editor = this.editor();
    if (!data.length || !editor || editor.isDestroyed) {
      return;
    }

    const scrollableParent = this.scrollableParent();
    const parentRect = scrollableParent?.getBoundingClientRect();
    const viewportTop = parentRect?.top ?? 0;
    const viewportBottom = parentRect?.bottom ?? window.innerHeight;
    const activeIds = new Set<string>();

    const positions: Array<{ id: string; top: number }> = [];
    for (const item of data) {
      const element = editor.view.dom.querySelector(`[data-toc-id="${item.id}"]`);
      if (!element) {
        continue;
      }
      positions.push({ id: item.id, top: element.getBoundingClientRect().top });
    }

    for (let i = 0; i < positions.length; i++) {
      const sectionTop = positions[i].top;
      const sectionBottom = i < positions.length - 1 ? positions[i + 1].top : Number.POSITIVE_INFINITY;

      const isVisible = sectionTop < viewportBottom && sectionBottom > viewportTop;
      if (isVisible) {
        activeIds.add(positions[i].id);
      }
    }

    if (activeIds.size === 0 && data.length > 0) {
      activeIds.add(data[0].id);
    }

    this.activeAnchorIds.set(activeIds);
  }
}
